import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Instagram, Mail, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-24 md:pt-32">
      <Seo
        title="Contact — Reserve 30 Parfums"
        description="Questions on orders, fragrances or partnerships — write to contact@reserve30.in. We respond within 24 hours."
      />
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 pb-28 grid md:grid-cols-12 gap-14">
        <Reveal className="md:col-span-5">
          <p className="overline-label">Contact</p>
          <h1 className="font-serif-brand text-5xl sm:text-6xl font-light mt-4 leading-[0.95]">
            Speak with<br />the house.
          </h1>
          <p className="mt-6 text-sm md:text-base text-black/60 leading-relaxed max-w-sm">
            Questions on orders, fragrances or partnerships — write to us and we'll respond within a day.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <a href="mailto:contact@reserve30.in" data-testid="contact-email-link" className="flex items-center gap-3 link-line w-fit">
              <Mail size={16} strokeWidth={1.5} className="text-[#BA8D53]" /> contact@reserve30.in
            </a>
            <a href="https://wa.me/919975585549" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 link-line w-fit">
              <MessageCircle size={16} strokeWidth={1.5} className="text-[#BA8D53]" /> +91 99755 85549
            </a>
            <a href="https://instagram.com/reserve30parfums" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="flex items-center gap-3 link-line w-fit">
              <Instagram size={16} strokeWidth={1.5} className="text-[#BA8D53]" /> @reserve30parfums
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
            <input required data-testid="contact-name-input" className="input-minimal" placeholder="Full name" value={form.name} onChange={set("name")} />
            <input required type="email" data-testid="contact-email-input" className="input-minimal" placeholder="Email address" value={form.email} onChange={set("email")} />
            <input data-testid="contact-subject-input" className="input-minimal" placeholder="Subject (optional)" value={form.subject} onChange={set("subject")} />
            <textarea required rows={5} data-testid="contact-message-input" className="input-minimal resize-none" placeholder="Your message" value={form.message} onChange={set("message")} />
            <button type="submit" disabled={loading} data-testid="contact-submit-button" className="btn-ink disabled:opacity-50">
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
